import React, { useEffect, useState, useRef } from 'react';
import { useStore } from '../hooks/useStore';
import { Search, X, ChevronDown, ArrowUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { DoorStrip, DOOR_PALETTE, WOOD_TEXTURE_CSS } from '../components/DoorStrip';
import { formatPrice } from '../lib/formatPrice';
import { PromoModal } from '../components/PromoModal';
const CATEGORY_META: Record<string, {
  label: string;
  color: string;
}> = {
  Entradas: {
    label: 'Para abrir el apetito',
    color: DOOR_PALETTE[0]
  },
  'Temporada China': {
    label: 'Fusión oriental de temporada',
    color: DOOR_PALETTE[2]
  },
  'Rincón Peruano': {
    label: 'Sabores del Perú',
    color: DOOR_PALETTE[5]
  },
  Ensaladas: {
    label: 'Frescura en cada bocado',
    color: DOOR_PALETTE[3]
  },
  Carnes: {
    label: 'Del fuego a tu mesa',
    color: DOOR_PALETTE[0]
  },
  'Rincón del Chef': {
    label: 'Creaciones especiales',
    color: DOOR_PALETTE[1]
  },
  Pastas: {
    label: 'Al dente y con sabor',
    color: DOOR_PALETTE[2]
  },
  Mofongos: {
    label: 'Tradición dominicana',
    color: DOOR_PALETTE[0]
  },
  'Del Mar': {
    label: 'Lo mejor del océano',
    color: DOOR_PALETTE[3]
  },
  Sopas: {
    label: 'Calidez en cada cucharada',
    color: DOOR_PALETTE[2]
  },
  Hamburguesas: {
    label: 'Jugosas y artesanales',
    color: DOOR_PALETTE[0]
  },
  Pizzas: {
    label: 'Horno de leña artesanal',
    color: DOOR_PALETTE[2]
  },
  'Esquina Mexicana': {
    label: 'Sabores de México',
    color: DOOR_PALETTE[0]
  },
  'Tabla Parrillera': {
    label: 'Para compartir en grande',
    color: DOOR_PALETTE[1]
  },
  Guarniciones: {
    label: 'El complemento perfecto',
    color: DOOR_PALETTE[3]
  },
  Cocteles: {
    label: 'Mezclas de autor',
    color: DOOR_PALETTE[4]
  },
  Shots: {
    label: 'Rápidos y directos',
    color: DOOR_PALETTE[0]
  },
  Frozen: {
    label: 'Fríos y refrescantes',
    color: DOOR_PALETTE[3]
  },
  Refrescos: {
    label: 'Sin alcohol, con sabor',
    color: DOOR_PALETTE[3]
  },
  Cervezas: {
    label: 'Nacionales e importadas',
    color: DOOR_PALETTE[5]
  },
  Ron: {
    label: 'El espíritu dominicano',
    color: DOOR_PALETTE[2]
  },
  Whisky: {
    label: 'Selección premium',
    color: DOOR_PALETTE[2]
  },
  'Vino Blanco': {
    label: 'Blancos seleccionados',
    color: DOOR_PALETTE[5]
  },
  'Vino Tinto': {
    label: 'Tintos de autor',
    color: DOOR_PALETTE[0]
  },
  'Platos Fuertes': {
    label: 'Las estrellas de la casa',
    color: DOOR_PALETTE[1]
  },
  Bebidas: {
    label: 'Refrescantes y esenciales',
    color: DOOR_PALETTE[2]
  },
  Postres: {
    label: 'El dulce final',
    color: DOOR_PALETTE[4]
  }
};
const DEFAULT_META = {
  label: 'Nuestra selección',
  color: DOOR_PALETTE[1]
};
const ALL_CATEGORY = 'Todos';
const ALL_COLOR = '#ca8a04'; // amarillo dorado
// Returns a color cycling through DOOR_PALETTE based on category position
function getCategoryColor(cat: string, cats: string[]): string {
  const idx = cats.indexOf(cat);
  return DOOR_PALETTE[idx >= 0 ? idx % DOOR_PALETTE.length : 0];
}
type MenuPageProps = {
  onNavigate?: (page: string) => void;
};
export function MenuPage({
  onNavigate
}: MenuPageProps) {
  const {
    menuItems,
    categories,
    menuLoading
  } = useStore();
  const [activeCategory, setActiveCategory] = useState<string>(ALL_CATEGORY);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [showPromo, setShowPromo] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const listTopRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Show scroll-to-top button after scrolling down
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToTop = () => window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
  // Show promo on first visit per session
  useEffect(() => {
    const seen = sessionStorage.getItem('cochera_promo_seen');
    if (!seen) {
      const timer = setTimeout(() => setShowPromo(true), 600);
      return () => clearTimeout(timer);
    }
  }, []);
  const handleClosePromo = () => {
    setShowPromo(false);
    sessionStorage.setItem('cochera_promo_seen', '1');
  };
  const filteredItems = menuItems.filter(item => {
    if (!item.available) return false;
    const matchesSearch = searchQuery === '' || item.name.toLowerCase().includes(searchQuery.toLowerCase()) || item.description.toLowerCase().includes(searchQuery.toLowerCase()) || item.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = searchQuery !== '' || activeCategory === ALL_CATEGORY || item.category === activeCategory;
    return matchesSearch && matchesCategory;
  });
  const isSearching = searchQuery !== '';
  const displayCategory = isSearching ? ALL_CATEGORY : activeCategory;
  const meta = displayCategory === ALL_CATEGORY ? {
    label: isSearching ? `Resultados para "${searchQuery}"` : 'Todo el menú',
    color: '#c0185a'
  } : {
    label: (CATEGORY_META[activeCategory] ?? DEFAULT_META).label,
    color: getCategoryColor(activeCategory, categories)
  };
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) searchInputRef.current.focus();
  }, [isSearchOpen]);
  // Lock body scroll when category picker is open
  useEffect(() => {
    document.body.style.overflow = isCategoryOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isCategoryOpen]);
  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    setSearchQuery('');
    setIsSearchOpen(false);
    setIsCategoryOpen(false);
    setTimeout(() => {
      if (window.scrollY > 200) listTopRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 50);
  };
  const activeMeta = activeCategory === ALL_CATEGORY ? {
    color: ALL_COLOR
  } : {
    color: getCategoryColor(activeCategory, categories)
  };
  return <div className="min-h-screen flex flex-col relative font-sans" style={{
    background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)'
  }} data-id="element-494">
      {/* ── PROMO MODAL ── */}
      <AnimatePresence data-id="element-495">
        {showPromo && <PromoModal onClose={handleClosePromo} data-id="element-496" />}
      </AnimatePresence>

      {/* ── STICKY NAV ── */}
      <div className="fixed top-0 left-0 right-0 z-50 border-b border-black/20 shadow-sm" style={{
      backgroundImage: WOOD_TEXTURE_CSS,
      backgroundColor: '#c0185a'
    }} data-id="element-497">
        {/* Tricolor strip top */}
        <div className="flex h-1" data-id="element-498">
          <div className="flex-1" style={{
          backgroundColor: '#002D62'
        }} data-id="element-499" />
          <div className="flex-1 bg-white" data-id="element-500" />
          <div className="flex-1" style={{
          backgroundColor: '#CE1126'
        }} data-id="element-501" />
        </div>

        <div className="max-w-4xl mx-auto px-3 flex items-center gap-2 py-2" data-id="element-502">
          {/* Flag */}
          <DominicanFlag className="w-6 h-4 rounded-sm flex-shrink-0 shadow-sm" data-id="element-503" />

          {/* Category selector button */}
          <button onClick={() => {
          setIsCategoryOpen(true);
          setIsSearchOpen(false);
        }} className="flex items-center gap-2 flex-1 min-w-0 py-1.5 px-3 rounded-xl transition-all active:scale-[0.98]" style={{
          backgroundColor: 'rgba(0,0,0,0.25)',
          border: '1.5px solid rgba(255,255,255,0.15)'
        }} data-id="element-504">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
            backgroundColor: activeMeta.color
          }} data-id="element-505" />
            <span className="font-bold text-sm truncate text-white/90" data-id="element-506">
              {activeCategory === ALL_CATEGORY ? 'Todo el menú' : activeCategory}
            </span>
            <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 ml-auto text-white/60" data-id="element-507" />
          </button>

          {/* Search toggle */}
          <button onClick={() => {
          setIsSearchOpen(!isSearchOpen);
          setIsCategoryOpen(false);
        }} className={`p-2 rounded-full transition-all flex-shrink-0 ${isSearchOpen ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'}`} data-id="element-508">
            {isSearchOpen ? <X className="w-4 h-4" data-id="element-509" /> : <Search className="w-4 h-4" data-id="element-510" />}
          </button>
        </div>

        {/* Search bar */}
        <AnimatePresence data-id="element-511">
          {isSearchOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden border-t border-white/10" style={{
          backgroundColor: 'rgba(0,0,0,0.3)'
        }} data-id="element-512">
              <div className="max-w-4xl mx-auto px-4 py-3" data-id="element-513">
                <div className="relative" data-id="element-514">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" data-id="element-515" />
                  <input ref={searchInputRef} type="text" placeholder="Buscar en todo el menú..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white/10 border border-white/20 focus:border-white/40 rounded-xl py-2.5 pl-9 pr-4 text-sm outline-none transition-all text-white placeholder-white/40" data-id="element-516" />
                </div>
                <p className="text-[10px] text-white/40 font-semibold mt-1.5 pl-1" data-id="element-517">
                  Buscando en todas las categorías
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* ── SCROLL TO TOP ── */}
      <AnimatePresence data-id="element-518">
        {showScrollTop && <motion.button initial={{
        opacity: 0,
        scale: 0.8,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.8,
        y: 10
      }} transition={{
        duration: 0.2
      }} onClick={scrollToTop} className="fixed bottom-6 right-4 z-40 w-11 h-11 rounded-full shadow-lg flex items-center justify-center active:scale-95 transition-transform" style={{
        backgroundColor: '#c0185a',
        backgroundImage: WOOD_TEXTURE_CSS
      }} aria-label="Volver arriba" data-id="element-519">
            <ArrowUp className="w-5 h-5 text-white" data-id="element-520" />
          </motion.button>}
      </AnimatePresence>

      {/* ── CATEGORY PICKER OVERLAY ── */}
      <AnimatePresence data-id="element-521">
        {isCategoryOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setIsCategoryOpen(false)} data-id="element-522" />

            {/* Bottom sheet */}
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col" data-id="element-523">
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-1 flex-shrink-0" data-id="element-524">
                <div className="w-10 h-1 bg-stone-200 rounded-full" data-id="element-525" />
              </div>

              {/* Header */}
              <div className="px-5 py-3 flex items-center justify-between border-b border-stone-100 flex-shrink-0" data-id="element-526">
                <div className="flex items-center gap-2" data-id="element-527">
                  <div className="w-4 h-4 text-stone-500" data-id="element-528" />
                  <h3 className="font-bold text-stone-900 text-base" data-id="element-529">
                    Categorías
                  </h3>
                  <span className="text-xs text-stone-400 font-semibold" data-id="element-530">
                    {categories.length + 1} secciones
                  </span>
                </div>
                <button onClick={() => setIsCategoryOpen(false)} className="p-1.5 rounded-full hover:bg-stone-100 text-stone-400" data-id="element-531">
                  <X className="w-4 h-4" data-id="element-532" />
                </button>
              </div>

              {/* Grid of categories */}
              <div className="overflow-y-auto flex-1 px-4 py-4" data-id="element-533">
                <div className="grid grid-cols-3 gap-2.5" data-id="element-534">
                  {/* Todos card */}
                  <button onClick={() => handleCategoryChange(ALL_CATEGORY)} className={`relative rounded-2xl p-3 text-left transition-all active:scale-95 overflow-hidden ${activeCategory === ALL_CATEGORY ? 'ring-2 ring-offset-1 ring-yellow-500' : ''}`} style={{
                backgroundColor: ALL_COLOR
              }} data-id="element-535">
                    <div className="text-black/15 text-3xl font-extrabold leading-none tabular-nums absolute right-2 bottom-1 select-none" data-id="element-536">
                      {String(menuItems.filter(i => i.available).length).padStart(2, '0')}
                    </div>
                    <p className="font-extrabold text-white text-sm leading-tight relative z-10 drop-shadow-sm" data-id="element-537">
                      Todo
                    </p>
                    <p className="text-white/70 text-[10px] font-semibold mt-0.5 relative z-10" data-id="element-538">
                      el menú
                    </p>
                  </button>

                  {/* Category cards */}
                  {categories.map(cat => {
                const m = CATEGORY_META[cat] ?? DEFAULT_META;
                const color = getCategoryColor(cat, categories);
                const count = menuItems.filter(i => i.category === cat && i.available).length;
                const isActive = activeCategory === cat;
                return <button key={cat} onClick={() => handleCategoryChange(cat)} className={`relative rounded-2xl p-3 text-left transition-all active:scale-95 overflow-hidden ${isActive ? 'ring-2 ring-offset-1' : ''}`} style={{
                  backgroundColor: color,
                  backgroundImage: WOOD_TEXTURE_CSS,
                  ...(isActive ? {
                    ringColor: color
                  } as any : {})
                }} data-id="element-539">
                        <div className="text-black/10 text-3xl font-extrabold leading-none tabular-nums absolute right-2 bottom-1 select-none mix-blend-overlay" data-id="element-540">
                          {String(count).padStart(2, '0')}
                        </div>
                        <p className="font-extrabold text-white text-xs leading-tight relative z-10 drop-shadow-sm" data-id="element-541">
                          {cat}
                        </p>
                        <p className="text-white/60 text-[9px] font-semibold mt-0.5 relative z-10 leading-tight line-clamp-1" data-id="element-542">
                          {m.label}
                        </p>
                      </button>;
              })}
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* ── CONTENIDO PRINCIPAL ── */}
      <div className="relative z-10 flex flex-col flex-1" style={{
      background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)'
    }} data-id="element-543">
        <div className="absolute inset-0 bg-stone-100/90 pointer-events-none z-0" data-id="element-544" />

        {/* ── HERO HEADER ── */}
        <div className="relative bg-[#0a0a0a] overflow-hidden shadow-lg border-b border-white/10 z-10" style={{
        paddingTop: 56
      }} data-id="element-545">
          <div className="absolute inset-0 opacity-20 transition-colors duration-500" style={{
          background: `radial-gradient(ellipse at bottom center, ${meta.color} 0%, transparent 70%)`
        }} data-id="element-546" />
          <div className="relative z-10 text-center px-6 pt-4 pb-4" data-id="element-547">
            <img src="https://cdn.magicpatterns.com/uploads/ewfDCj5QKNDNeQRsTcwjBG/logo_de_Junquito_Gas_(4).ai_(4).png" alt="La Cochera" className="h-16 mx-auto" data-id="element-548" />
          </div>

          <DoorStrip height="h-3" data-id="element-549" />
        </div>

        {/* ── CATEGORY BANNER (single category only) ── */}
        <AnimatePresence mode="wait" data-id="element-550">
          {(displayCategory !== ALL_CATEGORY || isSearching) && <motion.div key={displayCategory + (isSearching ? searchQuery : '')} initial={{
          opacity: 0,
          y: -5
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="relative overflow-hidden shadow-sm z-20 border-b border-black/10" style={{
          backgroundColor: meta.color,
          backgroundImage: WOOD_TEXTURE_CSS
        }} ref={listTopRef} data-id="element-551">
              <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between relative z-10" data-id="element-552">
                <div data-id="element-553">
                  <div className="flex items-center gap-3 mb-1" data-id="element-554">
                    <span className="w-2 h-2 rounded-full flex-shrink-0 ring-2 ring-white/30 shadow-sm" style={{
                  backgroundColor: 'rgba(255,255,255,0.9)'
                }} data-id="element-555" />
                    <h2 className="font-display text-2xl font-extrabold text-white tracking-tight drop-shadow-sm" data-id="element-556">
                      {isSearching ? 'Búsqueda' : displayCategory}
                    </h2>
                  </div>
                  <p className="text-white/80 text-xs font-bold uppercase tracking-widest pl-5 font-sans" data-id="element-557">
                    {meta.label}
                  </p>
                </div>
                <span className="font-display text-6xl font-extrabold text-white/10 leading-none select-none tabular-nums" data-id="element-558">
                  {String(filteredItems.length).padStart(2, '0')}
                </span>
              </div>
            </motion.div>}
        </AnimatePresence>

        {displayCategory === ALL_CATEGORY && !isSearching && <div ref={listTopRef} data-id="element-559" />}

        {/* ── ITEMS ── */}
        <div className="flex-1 max-w-4xl mx-auto w-full px-4 py-8 pb-16 relative z-10" data-id="element-560">
          {menuLoading ? <div className="space-y-3" data-id="element-561">
              {[...Array(6)].map((_, i) => <div key={i} className="bg-white rounded-xl border border-stone-200/80 shadow-sm overflow-hidden animate-pulse" style={{
            animationDelay: `${i * 80}ms`
          }} data-id="element-562">
                  <div className="flex items-start gap-3 p-5" data-id="element-563">
                    <div className="flex-1 space-y-2" data-id="element-564">
                      <div className="h-4 bg-stone-200 rounded w-2/3" data-id="element-565" />
                      <div className="h-3 bg-stone-100 rounded w-full" data-id="element-566" />
                      <div className="h-3 bg-stone-100 rounded w-4/5" data-id="element-567" />
                    </div>
                    <div className="h-7 w-24 bg-stone-200 rounded-md flex-shrink-0" data-id="element-568" />
                  </div>
                </div>)}
            </div> : filteredItems.length === 0 ? <div className="text-center py-20 text-stone-500 bg-white rounded-2xl border-2 border-dashed border-stone-300/60 shadow-sm" data-id="element-569">
              <Search className="w-10 h-10 mx-auto mb-4 opacity-20" data-id="element-570" />
              <p className="font-bold text-sm" data-id="element-571">
                {searchQuery ? `Sin resultados para "${searchQuery}"` : 'No hay platos disponibles.'}
              </p>
              <p className="text-xs mt-1 opacity-60" data-id="element-572">
                Intenta con otra categoría o búsqueda.
              </p>
            </div> : activeCategory === ALL_CATEGORY || isSearching ?
        // ── GROUPED VIEW — ordered by store categories ──
        <motion.div key={displayCategory + searchQuery} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3
        }} className="space-y-8" data-id="element-573">
              {(() => {
            // Build groups respecting the store's category order
            const groups: {
              category: string;
              items: typeof filteredItems;
            }[] = [];
            // First pass: categories in store order
            categories.forEach(cat => {
              const catItems = filteredItems.filter(i => i.category === cat).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
              if (catItems.length > 0) {
                groups.push({
                  category: cat,
                  items: catItems
                });
              }
            });
            // Second pass: any items whose category isn't in the store list yet
            filteredItems.forEach(item => {
              if (!categories.includes(item.category)) {
                const existing = groups.find(g => g.category === item.category);
                if (existing) {
                  existing.items.push(item);
                } else {
                  groups.push({
                    category: item.category,
                    items: [item]
                  });
                }
              }
            });
            return groups.map((group, gIdx) => {
              const gMeta = CATEGORY_META[group.category] ?? DEFAULT_META;
              const gColor = getCategoryColor(group.category, categories);
              return <motion.div key={group.category} initial={{
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.25,
                delay: gIdx * 0.04
              }} data-id="element-574">
                      <div className="rounded-xl overflow-hidden mb-4 shadow-sm" style={{
                  backgroundColor: gColor,
                  backgroundImage: WOOD_TEXTURE_CSS
                }} data-id="element-575">
                        <div className="px-5 py-3 flex items-center justify-between" data-id="element-576">
                          <div className="flex items-center gap-2.5" data-id="element-577">
                            <span className="w-2 h-2 rounded-full flex-shrink-0 ring-2 ring-white/30" style={{
                        backgroundColor: 'rgba(255,255,255,0.9)'
                      }} data-id="element-578" />
                            <h3 className="font-display text-lg font-extrabold text-white tracking-tight drop-shadow-sm" data-id="element-579">
                              {group.category}
                            </h3>
                          </div>
                          <div className="flex items-center gap-3" data-id="element-580">
                            <p className="text-white/70 text-[10px] font-bold uppercase tracking-widest hidden sm:block" data-id="element-581">
                              {gMeta.label}
                            </p>
                            <span className="font-display text-3xl font-extrabold text-white/20 leading-none select-none tabular-nums" data-id="element-582">
                              {String(group.items.length).padStart(2, '0')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-583">
                        {group.items.map((item, idx) => <motion.div key={item.id} initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.18,
                    delay: Math.min(idx * 0.025, 0.2),
                    ease: 'easeOut'
                  }} className="relative bg-white rounded-xl overflow-hidden border border-stone-200/80 shadow-sm" data-id="element-584">
                            <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{
                      backgroundColor: gColor
                    }} data-id="element-585" />
                            <div className="pl-6 pr-5 py-5" data-id="element-586">
                              <div className="flex items-start justify-between gap-3 mb-2" data-id="element-587">
                                <h4 className="font-bold text-stone-900 text-base leading-snug flex-1" data-id="element-588">
                                  {item.name}
                                </h4>
                                <div className="flex-shrink-0 px-3 py-1 rounded-md text-sm font-extrabold tabular-nums border" style={{
                          backgroundColor: `${gColor}15`,
                          color: gColor,
                          borderColor: `${gColor}40`
                        }} data-id="element-589">
                                  RD$ {formatPrice(item.price)}
                                </div>
                              </div>
                              {item.description && <p className="text-stone-500 text-sm leading-relaxed" data-id="element-590">
                                  {item.description}
                                </p>}
                            </div>
                          </motion.div>)}
                      </div>
                    </motion.div>;
            });
          })()}
            </motion.div> :
        // ── SINGLE CATEGORY VIEW ──
        <motion.div key={displayCategory} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3
        }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-591">
              {[...filteredItems].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).map((item, idx) => <motion.div key={item.id} initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.2,
            delay: Math.min(idx * 0.03, 0.3),
            ease: 'easeOut'
          }} className="relative bg-white rounded-xl overflow-hidden border border-stone-200/80 shadow-sm" data-id="element-592">
                    <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{
              backgroundColor: meta.color
            }} data-id="element-593" />
                    <div className="pl-6 pr-5 py-5" data-id="element-594">
                      <div className="flex items-start justify-between gap-3 mb-2" data-id="element-595">
                        <h3 className="font-bold text-stone-900 text-lg leading-snug flex-1" data-id="element-596">
                          {item.name}
                        </h3>
                        <div className="flex-shrink-0 px-3 py-1 rounded-md text-sm font-extrabold tabular-nums border" style={{
                  backgroundColor: `${meta.color}15`,
                  color: meta.color,
                  borderColor: `${meta.color}40`
                }} data-id="element-597">
                          RD$ {formatPrice(item.price)}
                        </div>
                      </div>
                      {item.description && <p className="text-stone-500 text-sm leading-relaxed" data-id="element-598">
                          {item.description}
                        </p>}
                    </div>
                  </motion.div>)}
            </motion.div>}
        </div>
      </div>

      {/* ── BOTTOM STRIP ── */}
      <div className="relative z-10 border-t border-stone-200" data-id="element-599">
        <DoorStrip inverted height="h-5" data-id="element-600" />
      </div>

      {/* ── FOOTER ── */}
      <div className="bg-black py-8 px-6 text-center relative z-10" data-id="element-601">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPSI0IiBoZWlnaHQ9IjQiIHZpZXdCb3g9IjAgMCA0IDQiPjxwYXRoIGZpbGw9IiNmZmZZmZmIiBvcGFjaXR5PSIwLjA1IiBkPSJNMSAzaDF2MUgxVjN6bTIgMGgxdjFIM1YzXSIvPjwvc3ZnPg==')] opacity-10" data-id="element-602" />
        <p className="text-white/70 text-xs font-bold uppercase tracking-widest" data-id="element-603">
          Todos los precios incluyen impuestos
        </p>
      </div>
    </div>;
}
// Dominican flag SVG component
function DominicanFlag({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 30 20" className={className} xmlns="http://www.w3.org/2000/svg" data-id="element-604">
      {/* Top-left: blue */}
      <rect x="0" y="0" width="13" height="9" fill="#002D62" data-id="element-605" />
      {/* Top-right: red */}
      <rect x="17" y="0" width="13" height="9" fill="#CE1126" data-id="element-606" />
      {/* Bottom-left: red */}
      <rect x="0" y="11" width="13" height="9" fill="#CE1126" data-id="element-607" />
      {/* Bottom-right: blue */}
      <rect x="17" y="11" width="13" height="9" fill="#002D62" data-id="element-608" />
      {/* White cross */}
      <rect x="0" y="8.5" width="30" height="3" fill="white" data-id="element-609" />
      <rect x="13" y="0" width="4" height="20" fill="white" data-id="element-610" />
    </svg>;
}